import { useCallback, useEffect, useMemo, useRef } from 'react';

import { AlertTriangleIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { toast } from 'sonner';

import {
  ADD_CARD,
  MODAL_CONFIRMATION,
  MODAL_UPGRADE_PLAN,
} from '@/components/organisms/Modals/ModalConductor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { getPercentage } from '@/modules/billing/hooks/useBillingState';
import { CURRENCY_OPTIONS, CurrencyCode, PlanId } from '@/modules/billing/model';
import { enableConfetti } from '@/redux/confetti/actions';
import { popModal, pushModal } from '@/redux/modals/actions';
import { isModalOpen } from '@/redux/modals/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { CONTACT_SALES_URL } from '@/util/constants';
import { addDaysToDate, formatDate } from '@/util/dateUtils';

import { InvoiceDetailsTable } from './InvoiceDetailsTable';
import { InvoiceSummary } from './InvoiceSummary';

type FormValues = { plan: PlanId };

export const PlanFieldset = () => {
  const { t } = useTranslation();
  const {
    upcomingInvoice,
    billingState,
    customer,
    paymentMethods,
    createSubscriptionAsync,
    deleteSubscriptionAsync,
    updateSubscription,
    refetchBillingState,
    isCreateSubscriptionPending,
  } = useBilling();

  const { days_left, next_payment } = billingState ?? {};

  const currentPeriodEnds = useMemo(
    () => formatDate(billingState?.current_period_ends, 'MMM d y'),
    [billingState?.current_period_ends]
  );

  const nextPayment = useMemo(() => {
    const adjustedDate = addDaysToDate(next_payment, -1);
    return adjustedDate ? formatDate(adjustedDate, 'MMM d yyyy') : '';
  }, [next_payment]);

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({ mode: 'onChange' });

  const { state: locationState } = useLocation();
  const { shouldTriggerAction = 1 } = locationState || {};
  const lastTriggeredAction = useRef(1);
  const modalOpen = useSelector(isModalOpen);

  useEffect(() => {
    if (billingState?.plan_id) {
      reset({ plan: billingState.plan_id });
    }
  }, [billingState?.plan_id, reset]);

  const dispatch = useDispatch();
  const handleResumePlan = useCallback(async () => {
    if (!paymentMethods?.length) {
      return dispatch(
        pushModal(ADD_CARD, {
          onSuccess: async () => {
            await createSubscriptionAsync({ planId: billingState?.plan_id });
            dispatch(popModal());
            toast.success(t('billing.plan_started'));
            dispatch(enableConfetti());
            trackEvent(EventName.ResumePlan);
          },
        })
      );
    } else {
      await createSubscriptionAsync({ planId: billingState?.plan_id });
      trackEvent(EventName.ResumePlan);
      return;
    }
  }, [billingState?.plan_id, createSubscriptionAsync, dispatch, paymentMethods?.length, t]);

  const handleStart = useCallback(async () => {
    const handleSubscriptionChange = async (plan: PlanId, currency?: CurrencyCode) => {
      if (billingState?.plan_id === 'trial') {
        await createSubscriptionAsync({ planId: plan, currency });
      } else {
        await updateSubscription(plan);
      }
      dispatch(popModal());
      toast.success(t('billing.plan_started'));
      dispatch(enableConfetti());
      trackEvent(EventName.ChangePlan);
      return;
    };

    // Attempt subscription first to provision the Stripe customer on the correct connected account.
    // If no payment method exists yet, catch the 400 and open ADD_CARD with the resolved stripeAccount.
    const onSuccessCallback = async (plan: PlanId, currency: CurrencyCode) => {
      try {
        await handleSubscriptionChange(plan, currency);
      } catch (error) {
        const statusCode = (error as { statusCode?: number }).statusCode;
        if (statusCode === 400 && (error as Error).message?.includes('no attached payment')) {
          await refetchBillingState();
          dispatch(popModal());
          dispatch(
            pushModal(ADD_CARD, { onSuccess: () => handleSubscriptionChange(plan, currency) })
          );
        } else {
          throw error;
        }
      }
    };

    dispatch(pushModal(MODAL_UPGRADE_PLAN, { onSuccess: onSuccessCallback }));
  }, [
    billingState?.plan_id,
    createSubscriptionAsync,
    dispatch,
    refetchBillingState,
    t,
    updateSubscription,
  ]);

  const handleChangePlan = useCallback(() => {
    const handleSubscriptionChange = async (plan: PlanId) => {
      await updateSubscription(plan);
      dispatch(popModal());
      dispatch(enableConfetti());
      trackEvent(EventName.ChangePlan);
    };

    const onSuccessCallback = async (plan: PlanId) => {
      if (!paymentMethods?.length) {
        return dispatch(pushModal(ADD_CARD, { onSuccess: handleSubscriptionChange }));
      } else {
        await handleSubscriptionChange(plan);
      }
    };

    dispatch(pushModal(MODAL_UPGRADE_PLAN, { onSuccess: onSuccessCallback }));
  }, [dispatch, paymentMethods?.length, updateSubscription]);

  const handleCancel = useCallback(() => {
    if (!billingState || billingState.plan_type !== 'stripe') {
      return;
    }
    dispatch(
      pushModal(MODAL_CONFIRMATION, {
        title: t('billing.plan_cancel'),
        danger: true,
        primaryButtonText: t('common.continue'),
        secondaryButtonText: t('common.back'),
        subtitle: t('billing.last_payment_subtitle', { 0: nextPayment }),
        callout: { children: t('billing.callout_ai_agent'), variant: 'error' },
        // Awaited so the dialog's spinner tracks the request and stops if it fails.
        onConfirm: async () => {
          await deleteSubscriptionAsync();
          dispatch(popModal());
          trackEvent(EventName.CancelPlan);
        },
      })
    );
  }, [billingState, dispatch, t, nextPayment, deleteSubscriptionAsync]);

  const statusMessage = useMemo(() => {
    if (!billingState) {
      return null;
    }

    if (billingState.has_expired) {
      return billingState?.plan_type === 'trial' ? (
        <span className="flex items-center gap-1 text-sm text-destructive">
          <AlertTriangleIcon size={16} />
          {t('billing.plan_trial_expired')}
        </span>
      ) : (
        <span className="text-sm text-destructive">
          {t('billing.plan_expired', { 0: billingState.plan_name })}
        </span>
      );
    }

    if (billingState.is_canceled) {
      return (
        <span className="text-sm text-warning">
          {t('billing.plan_cancelled', { 0: billingState.plan_name, 1: currentPeriodEnds })}
        </span>
      );
    }

    if (billingState.plan_type === 'trial') {
      return (
        <span className="text-sm text-foreground-muted">
          {t('billing.plan_trial_expires_in', { 0: billingState?.days_left })}
        </span>
      );
    }

    return null;
  }, [billingState, currentPeriodEnds, t]);

  const showCancelLink = useMemo(() => {
    return (
      billingState &&
      billingState.plan_type === 'stripe' &&
      !billingState.is_canceled &&
      !billingState.has_expired
    );
  }, [billingState]);

  const showContactSales = useMemo(() => {
    return (
      !billingState ||
      billingState.plan_type === 'trial' ||
      billingState.is_canceled ||
      billingState.plan_type === 'stripe'
    );
  }, [billingState]);

  const primaryAction: {
    button: React.ComponentPropsWithoutRef<typeof Button>;
    handler: (e?: React.FormEvent<HTMLFormElement>) => void;
  } = useMemo(() => {
    const isTrial = billingState?.plan_type === 'trial';
    const isCustom = billingState?.plan_type === 'custom';
    const hasExpired = billingState?.has_expired;
    const isCanceled = billingState?.is_canceled;

    const getDefaultAction = () => ({
      button: {
        children: isTrial ? `✨ ${t('billing.upgrade')}` : t('billing.choose_plan'),
        disabled: !isDirty && !isValid,
        isLoading: isCreateSubscriptionPending,
      },
      handler: handleSubmit(handleStart),
    });

    const getCustomPlanAction = () => ({
      button: { children: t('billing.contact_sales') },
      handler: () => {
        trackEvent(EventName.ContactSales);
        window.open(CONTACT_SALES_URL, '_blank');
      },
    });

    const getCanceledPlanAction = () => ({
      button: { children: t('billing.resume_plan'), isLoading: isCreateSubscriptionPending },
      handler: () => handleResumePlan(),
    });

    const getChangePlanAction = () => ({
      button: { children: t('billing.change_plan') },
      handler: handleChangePlan,
    });

    if (!billingState || isTrial || (billingState.plan_type !== 'custom' && hasExpired)) {
      return getDefaultAction();
    }

    if (isCustom && billingState.plan_id !== 'enterprise' && hasExpired) {
      return getCanceledPlanAction();
    }
    if (isCustom) {
      return getCustomPlanAction();
    }
    if (isCanceled) {
      return getCanceledPlanAction();
    }
    return getChangePlanAction();
  }, [
    billingState,
    t,
    isDirty,
    isValid,
    isCreateSubscriptionPending,
    handleSubmit,
    handleStart,
    handleResumePlan,
    handleChangePlan,
  ]);

  const shouldShowProgressbar = billingState?.plan_type === 'trial';

  useEffect(() => {
    if (
      !modalOpen &&
      shouldTriggerAction !== lastTriggeredAction.current &&
      billingState?.plan_id
    ) {
      lastTriggeredAction.current = shouldTriggerAction;
      primaryAction.handler();
    }
  }, [shouldTriggerAction, billingState?.plan_id, modalOpen, primaryAction]);

  const currency = CURRENCY_OPTIONS.find(
    (o) => o.code === upcomingInvoice?.currency?.toUpperCase()
  );

  if (!billingState) {
    return <Skeleton className="h-96 w-full rounded-lg" />;
  }

  return (
    <div className="flex flex-col gap-6 rounded-lg border border-border bg-background p-6">
      <div className="flex flex-col gap-3">
        {/* Plan */}
        <div className="flex items-center justify-between gap-8">
          <span className="text-sm text-foreground-muted">{t('billing.plan')}</span>
          <div className="flex items-center gap-2">
            <Badge type="secondary" flavour="informational" rounded={false}>
              {billingState.plan_name}
            </Badge>
            {billingState.is_canceled && (
              <Badge type="secondary" flavour="warning" rounded={false}>
                {t('billing.cancelled')}
              </Badge>
            )}
          </div>
        </div>
        {/* Currency - only for Stripe */}
        {billingState.plan_type === 'stripe' && currency && (
          <div className="flex items-center justify-between gap-8">
            <span className="text-sm text-foreground-muted">{t('billing.currency_label')}</span>
            <span className="text-right text-sm font-medium">
              {currency.flag} {t(currency.labelKey)}
            </span>
          </div>
        )}
        {/* Next payment - only for active Stripe plans */}
        {billingState.plan_type === 'stripe' && !billingState.is_canceled && nextPayment && (
          <div className="flex items-center justify-between gap-8">
            <span className="text-sm text-foreground-muted">{t('billing.next_payment_label')}</span>
            <span className="text-right text-sm font-medium">{nextPayment}</span>
          </div>
        )}
        {/* Status message */}
        {statusMessage}
      </div>

      {/* Trial progress bar */}
      {shouldShowProgressbar && (
        <Progress
          value={getPercentage(days_left)}
          indicatorClassName={billingState?.has_expired ? 'bg-destructive' : 'bg-informational'}
        />
      )}

      {/* Invoice */}
      {!shouldShowProgressbar && !!upcomingInvoice && (
        <>
          <InvoiceDetailsTable />
          <InvoiceSummary upcomingInvoice={upcomingInvoice} balance={customer?.balance} />
        </>
      )}

      {/* Google Marketplace info */}
      {billingState.plan_type === 'google' && (
        <p className="text-sm text-foreground-muted">{t('billing.google_marketplace_info')}</p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div>
          {showCancelLink && (
            <Button variant="destructive-ghost" onClick={handleCancel}>
              {t('billing.want_to_cancel')}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {showContactSales && (
            <Button
              variant="ghost"
              onClick={() => {
                trackEvent(EventName.ContactSales);
                window.open(CONTACT_SALES_URL, '_blank');
              }}
              className="text-informational hover:text-informational"
            >
              {t('billing.contact_sales')}
            </Button>
          )}
          <Button onClick={() => primaryAction.handler()} {...primaryAction.button} />
        </div>
      </div>
    </div>
  );
};
